import { useEffect, useRef } from "react";

import { Button } from "@/components/ui/button";
import { INBOX_ACCEPT, type InboxUploadSource } from "@/lib/documentInbox";

export interface QueuedInboxFile {
  id: string;
  file: File;
  source: InboxUploadSource;
  status: "waiting" | "sending" | "error";
  error: string | null;
}

function ImagePreview({ file }: { file: File }) {
  const image = useRef<HTMLImageElement>(null);
  useEffect(() => {
    if (!["image/jpeg", "image/png"].includes(file.type)) return;
    const objectUrl = URL.createObjectURL(file);
    if (image.current) image.current.src = objectUrl;
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  return ["image/jpeg", "image/png"].includes(file.type)
    ? <img ref={image} alt={`Pregled: ${file.name}`} className="h-16 w-16 shrink-0 rounded object-contain" />
    : null;
}

export function InboxFileQueue({ items, busy, onAdd, onRemove }: {
  items: QueuedInboxFile[];
  busy: boolean;
  onAdd: (files: File[], source: InboxUploadSource) => void;
  onRemove: (id: string) => void;
}) {
  const picker = useRef<HTMLInputElement>(null);
  const camera = useRef<HTMLInputElement>(null);
  function selected(input: HTMLInputElement, source: InboxUploadSource) {
    if (!busy) onAdd(Array.from(input.files ?? []), source);
    input.value = "";
  }

  return (
    <section className="flex flex-col gap-4" aria-label="Datoteke za slanje">
      <h2 className="text-[var(--text-card)] font-semibold">2. Dodajte dokumente</h2>
      <p className="text-muted-foreground">PDF, JPEG ili PNG, najviše 15 MB po datoteci.</p>
      <div
        className="avm-surface hidden rounded-[var(--radius-card)] border-2 border-dashed border-border p-8 text-center md:block"
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          if (!busy) onAdd(Array.from(event.dataTransfer.files), "drop");
        }}
      >Povucite datoteke ovdje</div>
      <div className="flex flex-wrap gap-3">
        <input ref={picker} aria-label="Datoteke" type="file" multiple accept={INBOX_ACCEPT} className="hidden" disabled={busy} onChange={(event) => selected(event.currentTarget, "picker")} />
        <Button type="button" variant="outline" disabled={busy} onClick={() => picker.current?.click()}>Odaberi datoteke</Button>
        <input ref={camera} aria-label="Fotografija dokumenta" type="file" accept="image/jpeg,image/png" capture="environment" className="hidden" disabled={busy} onChange={(event) => selected(event.currentTarget, "camera")} />
        <Button type="button" variant="outline" className="md:hidden" disabled={busy} onClick={() => camera.current?.click()}>Slikaj</Button>
      </div>
      <ul className="flex flex-col gap-3">
        {items.map((item) => (
          <li key={item.id} className={`flex items-center gap-3 rounded-md border p-3 ${item.error ? "border-destructive" : "border-border"}`}>
            <ImagePreview file={item.file} />
            <div className="min-w-0 flex-1">
              <p className="break-all font-medium">{item.file.name}</p>
              <p className="text-sm text-muted-foreground">{(item.file.size / (1024 * 1024)).toLocaleString("hr-HR", { maximumFractionDigits: 2 })} MB</p>
              <p className={item.error ? "text-sm text-destructive" : "text-sm"}>
                {item.error ?? (item.status === "sending" ? "Slanje…" : "Čeka slanje")}
              </p>
            </div>
            <Button type="button" variant="ghost" disabled={busy} aria-label={`Ukloni ${item.file.name}`} onClick={() => onRemove(item.id)}>Ukloni</Button>
          </li>
        ))}
      </ul>
    </section>
  );
}
